// Pantalla de validación de correcciones IA (Fase 38-42).
//
// Fase 38 — esqueleto: carga del reporte, conteos, navegación.
// Fase 39 — acciones: render de pendientes con CorrectionItemCard,
//   vista colapsada de auto-aplicadas, atom de decisiones local,
//   autocomplete del glosario.
// Fase 40 — audio: botón ▶ Escuchar 5s en cada card, video inline para MP4.
// Fase 41 — speakers no identificados: búsqueda en directorio,
//   creación inline de personas, asignación via speakerAssignmentsAtom.
// Fase 42 — confirmación: footer con conteo, botón confirmar,
//   panel de retroalimentación al glosario, POST confirm-corrections.

import { useEffect, useMemo, useRef, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import styled from "styled-components";

import {
  correctionDecisionsAtom,
  currentProcessingIdAtom,
  speakerAssignmentsAtom,
} from "../../state/atoms.ts";
import type { CorrectionDecision, SpeakerAssignment } from "../../state/atoms.ts";
import {
  confirmCorrections,
  fetchDirectory,
  fetchGlossary,
  fetchTextFromPar,
  getCorrectionReport,
  getProcessing,
  getProcessingFile,
  submitMetrics,
} from "../../platform/api.ts";
import type {
  AppliedCorrection,
  ConfirmCorrectionsRequest,
  DirectoryPerson,
  GetCorrectionReportResponse,
  GlossarySuggestion,
  GlossaryTerm,
  PendingCorrection,
  SpeakerIdentification,
} from "../../platform/api.ts";
import { CorrectionItemCard } from "../components/CorrectionItemCard.tsx";
import { GlossaryFeedbackPanel } from "../components/GlossaryFeedbackPanel.tsx";
import { UnidentifiedSpeakerCard } from "../components/UnidentifiedSpeakerCard.tsx";
import { useSamplePlayer } from "../hooks/useSamplePlayer.ts";
import { useStepTimer } from "../hooks/useStepTimer.ts";
import { useNavigateTo, useSyncParams } from "../routing.ts";
import { parseSrt } from "../../lib/parse-srt.ts";
import type { SrtSegment } from "../../lib/parse-srt.ts";

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
`;

const BackButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.color.border};
  color: ${({ theme }) => theme.color.textMuted};
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.sm};
  &:hover {
    background: ${({ theme }) => theme.color.background};
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => theme.font.size.lg};
  font-weight: 600;
  flex: 1;
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const SummaryBar = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
`;

const SummaryItem = styled.div<{ $kind: "applied" | "pending" | "speakers" }>`
  padding: 12px 20px;
  border-radius: 8px;
  background: ${({ theme, $kind }) =>
    $kind === "applied" ? theme.color.successMuted :
    $kind === "pending" ? theme.color.warningMuted :
    theme.color.surface};
  border: 1px solid ${({ theme, $kind }) =>
    $kind === "applied" ? theme.color.success :
    $kind === "pending" ? theme.color.warning :
    theme.color.border};
`;

const SummaryLabel = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const SummaryValue = styled.div`
  font-size: ${({ theme }) => theme.font.size.xl};
  font-weight: 700;
  margin-top: 4px;
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.font.size.md};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressText = styled.span`
  font-weight: ${({ theme }) => theme.font.weight.normal};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const CollapseToggle = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color.accent};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.sm};
  padding: 4px 0;
  margin-bottom: 16px;
  &:hover {
    text-decoration: underline;
  }
`;

const AppliedList = styled.div`
  margin-top: 12px;
`;

const ErrorBox = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.errorMuted};
  border: 1px solid ${({ theme }) => theme.color.error};
  color: ${({ theme }) => theme.color.error};
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.color.textMuted};
`;

const WallTime = styled.div`
  text-align: center;
  padding: 24px;
  color: ${({ theme }) => theme.color.textDim};
  font-size: ${({ theme }) => theme.font.size.xs};
`;

const Footer = styled.div`
  padding: 14px 20px;
  border-top: 1px solid ${({ theme }) => theme.color.border};
  background: ${({ theme }) => theme.color.surface};
  display: flex;
  align-items: center;
  gap: 24px;
`;

const FooterCounts = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  flex: 1;
`;

const ConfirmButton = styled.button<{ $disabled: boolean }>`
  padding: 10px 24px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid
    ${({ theme, $disabled }) =>
      $disabled ? theme.color.border : theme.color.success};
  background: ${({ theme, $disabled }) =>
    $disabled ? theme.color.background : theme.color.successMuted};
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.color.textDim : theme.color.success};
  font-size: ${({ theme }) => theme.font.size.md};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 1 : 0.85)};
  }
`;

const ConfirmError = styled.div`
  padding: 8px 20px;
  background: ${({ theme }) => theme.color.errorMuted};
  border-bottom: 1px solid ${({ theme }) => theme.color.error};
  color: ${({ theme }) => theme.color.error};
  font-size: ${({ theme }) => theme.font.size.sm};
`;

const SuccessBox = styled.div`
  padding: 32px 20px;
  text-align: center;
`;

const SuccessTitle = styled.div`
  font-size: ${({ theme }) => theme.font.size.lg};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ theme }) => theme.color.success};
  margin-bottom: 8px;
`;

const SuccessSub = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const SuccessActions = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 12px;
`;

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

type ScreenState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; report: GetCorrectionReportResponse };

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function CorrectionsScreen() {
  const processingId = useAtomValue(currentProcessingIdAtom);
  const navigateTo = useNavigateTo();
  useSyncParams();
  const [decisions, setDecisions] = useAtom(correctionDecisionsAtom);
  const [speakerAssignments, setSpeakerAssignments] = useAtom(speakerAssignmentsAtom);
  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [glossaryTerms, setGlossaryTerms] = useState<GlossaryTerm[]>([]);
  const [directory, setDirectory] = useState<DirectoryPerson[]>([]);
  const [srtSegments, setSrtSegments] = useState<SrtSegment[] | null>(null);
  const [showApplied, setShowApplied] = useState(false);

  // ── Métricas (Fase 61) ────────────────────────────────────────────────────
  const getElapsed = useStepTimer();

  // ── Confirm flow (Fase 42) ───────────────────────────────────────────────
  const [showFeedback, setShowFeedback] = useState(false);
  const [glossarySelection, setGlossarySelection] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  // ── Audio / sample player (Fase 40) ─────────────────────────────────────
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [sampleState, sampleControls] = useSamplePlayer(audioRef);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioFilename, setAudioFilename] = useState<string>("audio.wav");

  // Cargar reporte + glosario + audio al montar.
  useEffect(() => {
    if (!processingId) {
      setState({ kind: "error", message: "No hay reunión seleccionada." });
      return;
    }

    let cancelled = false;
    setState({ kind: "loading" });

    Promise.all([
      getCorrectionReport(processingId),
      fetchGlossary().catch(() => ({ terms: [] })),
      fetchDirectory().catch(() => ({ schemaVersion: 1 as const, version: 0, people: [] })),
      // Cargar audio en paralelo con el reporte
      getProcessing(processingId)
        .then((meta) => {
          const fn = meta.audioFilename ?? "audio.wav";
          if (!cancelled) setAudioFilename(fn);
          return getProcessingFile(processingId, fn);
        })
        .then((fileInfo) => {
          if (!cancelled) setAudioUrl(fileInfo.downloadUrl);
        })
        .catch((err) => {
          console.warn("[CorrectionsScreen] No se pudo cargar el audio:", err);
        }),
      // Cargar SRT corregido para estadísticas de speakers
      getProcessingFile(processingId, "srt-corrected.srt")
        .then((info) => fetchTextFromPar(info.downloadUrl))
        .then((text) => {
          if (!cancelled) setSrtSegments(parseSrt(text));
        })
        .catch((err) => {
          console.warn("[CorrectionsScreen] No se pudo cargar el SRT:", err);
        }),
    ])
      .then(([report, glossary, directoryResult]) => {
        if (!cancelled) {
          setState({ kind: "loaded", report });
          setGlossaryTerms(glossary.terms);
          setDirectory(directoryResult.people);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            kind: "error",
            message: err instanceof Error ? err.message : String(err),
          });
        }
      });

    return () => { cancelled = true; };
  }, [processingId]);

  const handleBack = () => navigateTo.meetings();

  const handleViewConfirmed = () => {
    if (processingId) navigateTo.viewer(processingId, "srt-confirmed.srt");
  };

  const handleDecision = (id: string, decision: CorrectionDecision) => {
    setDecisions((prev: Map<string, CorrectionDecision>) => {
      const next = new Map(prev);
      next.set(id, decision);
      return next;
    });
  };

  const handlePlaySample = (id: string, startTime: number) => {
    sampleControls.play(id, startTime, {
      duration: 1,
      paddingBefore: 2,
      paddingAfter: 2,
    });
  };

  const handleSpeakerAssign = (srtLabel: string, assignment: SpeakerAssignment | null) => {
    setSpeakerAssignments((prev) => {
      const next = new Map(prev);
      if (assignment === null) {
        next.delete(srtLabel);
      } else {
        next.set(srtLabel, assignment);
      }
      return next;
    });
  };

  const handleDirectoryRefresh = async () => {
    try {
      const result = await fetchDirectory();
      setDirectory(result.people);
    } catch {
      // No hacer nada — el directorio queda con su estado anterior
    }
  };

  const isMp4 = audioFilename === "audio.mp4";
  const videoUrl = isMp4 ? audioUrl : null;

  // ── Speaker stats from SRT ────────────────────────────────────────────

  /** Mapa srtLabel → { count, firstTimestamp } extraído del SRT corregido. */
  const speakerStats = useMemo(() => {
    const stats = new Map<string, { count: number; firstTimestamp: number }>();
    if (!srtSegments) return stats;

    for (const seg of srtSegments) {
      for (const line of seg.lines) {
        const match = line.match(/^\[([^\]]+)\]/);
        if (!match) continue;
        let label = match[1].trim();
        // Quitar sufijo " - no identificado" para matchear con srtLabel del reporte
        label = label.replace(/\s*-\s*no\s+identificado\s*$/i, "").trim();
        if (!label) continue;

        const existing = stats.get(label);
        if (existing) {
          existing.count++;
        } else {
          stats.set(label, { count: 1, firstTimestamp: seg.startTime });
        }
      }
    }
    return stats;
  }, [srtSegments]);

  /** Speakers cuyo método es "unidentified" y no tienen asignación en el directorio. */
  const unidentifiedSpeakers = useMemo((): SpeakerIdentification[] => {
    if (state.kind !== "loaded") return [];
    return state.report.speakers.filter(
      (s) => s.method === "unidentified" && s.assignedName === null,
    );
  }, [state]);

  // ── Derived counts ────────────────────────────────────────────────────

  const pendingWithDecisions = useMemo(() => {
    if (state.kind !== "loaded") return { resolved: 0, total: 0 };
    const pending = state.report.pending;
    const resolved = pending.filter((p) => decisions.has(p.id)).length;
    return { resolved, total: pending.length };
  }, [state, decisions]);

  /** true cuando todos los pendientes tienen decisión y todos los speakers sin identificar tienen asignación. */
  const isFullyResolved = useMemo(() => {
    if (state.kind !== "loaded") return false;
    const allPendingDecided = state.report.pending.every((p) => decisions.has(p.id));
    const allSpeakersAssigned = unidentifiedSpeakers.every((s) =>
      speakerAssignments.has(s.srtLabel),
    );
    return allPendingDecided && allSpeakersAssigned;
  }, [state, decisions, speakerAssignments, unidentifiedSpeakers]);

  const totalResolved = useMemo(() => {
    if (state.kind !== "loaded") return 0;
    const pendingResolved = state.report.pending.filter((p) => decisions.has(p.id)).length;
    const appliedResolved = state.report.applied.filter((a) => decisions.has(a.id)).length;
    const speakersResolved = unidentifiedSpeakers.filter((s) =>
      speakerAssignments.has(s.srtLabel),
    ).length;
    return pendingResolved + appliedResolved + speakersResolved;
  }, [state, decisions, speakerAssignments, unidentifiedSpeakers]);

  const totalItems = useMemo(() => {
    if (state.kind !== "loaded") return 0;
    return (
      state.report.pending.length +
      unidentifiedSpeakers.length
    );
  }, [state, unidentifiedSpeakers]);

  const handleGlossaryToggle = (itemId: string, included: boolean) => {
    setGlossarySelection((prev) => {
      const next = new Set(prev);
      if (included) {
        next.add(itemId);
      } else {
        next.delete(itemId);
      }
      return next;
    });
  };

  const handleStartConfirm = () => {
    // Inicializar selección de glosario: todos los edits pre-tickeados
    const edits = new Set<string>();
    const allItems = [
      ...(state.kind === "loaded" ? state.report.pending : []),
      ...(state.kind === "loaded" ? state.report.applied : []),
    ];
    for (const item of allItems) {
      const d = decisions.get(item.id);
      if (d?.kind === "edit" && d.correctedText) {
        edits.add(item.id);
      }
    }
    setGlossarySelection(edits);
    setShowFeedback(true);
    setConfirmError(null);
  };

  const handleConfirm = async () => {
    if (!processingId || state.kind !== "loaded") return;

    setConfirming(true);
    setConfirmError(null);

    // Enviar métricas de P2 (non-fatal)
    const counts = {
      correctionsAccepted: 0,
      correctionsRejected: 0,
      correctionsManual: 0,
    };
    for (const d of decisions.values()) {
      if (d.kind === "accept") counts.correctionsAccepted++;
      else if (d.kind === "skip") counts.correctionsRejected++;
      else if (d.kind === "edit") counts.correctionsManual++;
    }
    submitMetrics(processingId, {
      timeSpent: { p2: getElapsed() },
      counts,
    }).catch(() => {/* non-fatal */});

    // Construir glossarySuggestions
    const glossarySuggestions: GlossarySuggestion[] = [];
    const allItems = [...state.report.pending, ...state.report.applied];
    for (const item of allItems) {
      if (!glossarySelection.has(item.id)) continue;
      const d = decisions.get(item.id);
      if (d?.kind === "edit" && d.correctedText) {
        glossarySuggestions.push({
          term: d.correctedText,
          variants: [item.before],
          category: "global",
        });
      }
    }

    // Construir decisions record
    const decisionsRecord: ConfirmCorrectionsRequest["decisions"] = {};
    for (const [id, d] of decisions) {
      decisionsRecord[id] = d;
    }

    // Construir speakerAssignments record
    const assignmentsRecord: Record<string, { personId: string; personName: string }> = {};
    for (const [label, a] of speakerAssignments) {
      assignmentsRecord[label] = a;
    }

    const body: ConfirmCorrectionsRequest = {
      decisions: decisionsRecord,
      speakerAssignments: assignmentsRecord,
      glossarySuggestions,
    };

    try {
      await confirmCorrections(processingId, body);
      setConfirmed(true);
      setShowFeedback(false);
    } catch (err) {
      setConfirmError(
        err instanceof Error ? err.message : "Error al confirmar las correcciones.",
      );
    } finally {
      setConfirming(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (state.kind === "loading") {
    return (
      <Wrapper>
        <Header>
          <BackButton onClick={handleBack}>← Volver</BackButton>
          <Title>Cargando...</Title>
        </Header>
        <Content>
          <LoadingText>Cargando reporte de correcciones...</LoadingText>
        </Content>
      </Wrapper>
    );
  }

  if (state.kind === "error") {
    return (
      <Wrapper>
        <Header>
          <BackButton onClick={handleBack}>← Volver</BackButton>
          <Title>Error</Title>
        </Header>
        <Content>
          <ErrorBox>{state.message}</ErrorBox>
        </Content>
      </Wrapper>
    );
  }

  const { report } = state;
  const pendingItems: PendingCorrection[] = report.pending;
  const appliedItems: AppliedCorrection[] = report.applied;

  // ── Vista post-confirmación ──────────────────────────────────────────

  if (confirmed) {
    return (
      <Wrapper>
        <Header>
          <BackButton onClick={handleBack}>← Volver</BackButton>
          <Title>{report.meetingTitle}</Title>
        </Header>
        <Content>
          <SummaryBar>
            <SummaryItem $kind="applied">
              <SummaryLabel>Correcciones aplicadas</SummaryLabel>
              <SummaryValue>{report.metrics.totalApplied}</SummaryValue>
            </SummaryItem>
            <SummaryItem $kind="pending">
              <SummaryLabel>Correcciones revisadas</SummaryLabel>
              <SummaryValue>
                {pendingWithDecisions.resolved}/{pendingWithDecisions.total}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem $kind="speakers">
              <SummaryLabel>Speakers</SummaryLabel>
              <SummaryValue>{report.metrics.totalSpeakers}</SummaryValue>
            </SummaryItem>
          </SummaryBar>
          <SuccessBox>
            <SuccessTitle>Correcciones confirmadas</SuccessTitle>
            <SuccessSub>
              El SRT confirmado se guardó correctamente. Las decisiones y
              asignaciones quedaron aplicadas.
            </SuccessSub>
            <SuccessActions>
              <ConfirmButton $disabled={false} onClick={handleViewConfirmed}>
                👁 Ver SRT confirmado
              </ConfirmButton>
            </SuccessActions>
          </SuccessBox>
          <WallTime>
            Pipeline completado en {(report.pipelineWallTimeMs / 1000).toFixed(0)}s
          </WallTime>
        </Content>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          muted={isMp4}
        />
      )}
      <Header>
        <BackButton onClick={handleBack}>← Volver</BackButton>
        <Title>{report.meetingTitle}</Title>
      </Header>
      <Content>
        <SummaryBar>
          <SummaryItem $kind="applied">
            <SummaryLabel>Correcciones aplicadas</SummaryLabel>
            <SummaryValue>{report.metrics.totalApplied}</SummaryValue>
          </SummaryItem>
          <SummaryItem $kind="pending">
            <SummaryLabel>Pendientes de revisión</SummaryLabel>
            <SummaryValue>
              {pendingWithDecisions.resolved}/{pendingWithDecisions.total}
            </SummaryValue>
          </SummaryItem>
          <SummaryItem $kind="speakers">
            <SummaryLabel>
              Speakers ({report.metrics.speakersIdentified} id.,{" "}
              {report.metrics.speakersUnidentified} sin id.)
            </SummaryLabel>
            <SummaryValue>{report.metrics.totalSpeakers}</SummaryValue>
          </SummaryItem>
        </SummaryBar>

        {/* ── Speakers no identificados (Fase 41) ────────────────── */}

        {unidentifiedSpeakers.length > 0 && (
          <>
            <SectionTitle>
              Speakers sin identificar
              <ProgressText>
                ({unidentifiedSpeakers.length} pendiente
                {unidentifiedSpeakers.length !== 1 ? "s" : ""})
              </ProgressText>
            </SectionTitle>

            {unidentifiedSpeakers.map((speaker) => {
              const stats = speakerStats.get(speaker.srtLabel);
              const assignment = speakerAssignments.get(speaker.srtLabel);
              return (
                <UnidentifiedSpeakerCard
                  key={speaker.srtLabel}
                  speaker={speaker}
                  segmentCount={stats?.count ?? 0}
                  firstTimestamp={stats?.firstTimestamp ?? null}
                  directory={directory}
                  assignment={assignment}
                  onAssign={handleSpeakerAssign}
                  onDirectoryRefresh={handleDirectoryRefresh}
                  playingId={sampleState.playingId}
                  onPlaySample={audioUrl ? handlePlaySample : undefined}
                />
              );
            })}
          </>
        )}

        {/* ── Pendientes ──────────────────────────────────────────── */}

        <SectionTitle>
          Correcciones pendientes
          <ProgressText>
            ({pendingWithDecisions.resolved} de {pendingWithDecisions.total}{" "}
            revisadas)
          </ProgressText>
        </SectionTitle>

        {pendingItems.length === 0 ? (
          <LoadingText>
            ¡No hay correcciones pendientes! Todas las correcciones se aplicaron
            automáticamente.
          </LoadingText>
        ) : (
          pendingItems.map((item) => (
            <CorrectionItemCard
              key={item.id}
              item={item}
              decision={decisions.get(item.id)}
              glossaryTerms={glossaryTerms}
              onChange={handleDecision}
              playingId={sampleState.playingId}
              videoUrl={videoUrl}
              onPlaySample={audioUrl ? handlePlaySample : undefined}
            />
          ))
        )}

        {/* ── Auto-aplicadas (colapsadas) ─────────────────────────── */}

        {appliedItems.length > 0 && (
          <>
            <CollapseToggle onClick={() => setShowApplied(!showApplied)}>
              {showApplied ? "▲" : "▼"} Correcciones auto-aplicadas (
              {appliedItems.length})
            </CollapseToggle>

            {showApplied && (
              <AppliedList>
                {appliedItems.map((item) => (
                  <CorrectionItemCard
                    key={item.id}
                    item={item}
                    decision={decisions.get(item.id)}
                    glossaryTerms={glossaryTerms}
                    onChange={handleDecision}
                    playingId={sampleState.playingId}
                    videoUrl={videoUrl}
                    onPlaySample={audioUrl ? handlePlaySample : undefined}
                  />
                ))}
              </AppliedList>
            )}
          </>
        )}

        {/* ── Retroalimentación al glosario (Fase 42) ──────────────── */}

        {showFeedback && (
          <GlossaryFeedbackPanel
            decisions={decisions}
            pendingItems={pendingItems}
            appliedItems={appliedItems}
            selectedIds={glossarySelection}
            onToggle={handleGlossaryToggle}
          />
        )}

        <WallTime>
          Pipeline completado en {(report.pipelineWallTimeMs / 1000).toFixed(0)}s
        </WallTime>
      </Content>

      {/* ── Footer (Fase 42) ──────────────────────────────────────── */}

      {confirmError && <ConfirmError>{confirmError}</ConfirmError>}

      {!showFeedback ? (
        <Footer>
          <FooterCounts>
            Pendientes: {totalResolved}/{totalItems} resueltos
          </FooterCounts>
          <ConfirmButton
            $disabled={!isFullyResolved || confirming}
            disabled={!isFullyResolved || confirming}
            onClick={isFullyResolved ? handleStartConfirm : undefined}
          >
            {confirming ? "Confirmando..." : "✓ Confirmar correcciones"}
          </ConfirmButton>
        </Footer>
      ) : (
        <Footer>
          <FooterCounts>
            Revisá las sugerencias para el glosario
          </FooterCounts>
          <ConfirmButton
            $disabled={confirming}
            disabled={confirming}
            onClick={handleConfirm}
          >
            {confirming ? "Confirmando..." : "✓ Confirmar y enviar"}
          </ConfirmButton>
        </Footer>
      )}
    </Wrapper>
  );
}
